import { Broadcast } from 'features/broadcast/broadcastsSlice';
import { createSelector } from 'reselect';
import { RootState } from 'rootReducer';
import { EventMap } from 'features/events/eventsSlice';
import { isAccessLevelListed } from 'features/events/EventModel';

// Whether a broadcast should be treated as "live on the public channel".
//
// The fallback is the channel's public always-on source, so it is always
// allowed. A broadcast tied to a non-public event (unlisted / only_me) — or to
// an event we can't resolve (e.g. an only_me event that was removed from state
// for non-owner listeners) — must NOT count as a public live broadcast, so it
// can never displace, finish, or suppress the fallback. A broadcast with no
// associated event is treated as public (preserves existing behaviour for
// event-less broadcasts).
export const isBroadcastFromListedEvent = (
  broadcast: Broadcast | null | undefined,
  events: EventMap,
  privateEvents: EventMap
): boolean => {
  if (!broadcast) return false;
  if (broadcast.fallback) return true;
  if (!broadcast.eventId) return true;

  const event = events[broadcast.eventId] || privateEvents[broadcast.eventId];
  if (!event) return false;

  return isAccessLevelListed(event.accessLevel);
};

export const isBroadcastListed = (
  state: RootState,
  broadcast: Broadcast | null | undefined
): boolean => {
  return isBroadcastFromListedEvent(
    broadcast,
    state.events.events,
    state.events.privateEvents
  );
};

export const broadcastSelector = createSelector(
  [
    (state: RootState) => state.broadcasts.broadcasts,
    (state: RootState, uid: string | null) => uid
  ],
  (broadcasts, uid): Broadcast | null => {
    return broadcasts.find((broadcastEl) => broadcastEl.uid === uid) || null;
  },
  {
    memoizeOptions: {
      maxSize: 10
    }
  }
);

export const liveBroadcastsSelector = createSelector(
  [
    (state: RootState) => state.broadcasts.broadcasts,
    (state: RootState) => state.events.events,
    (state: RootState) => state.events.privateEvents
  ],
  (broadcasts, events, privateEvents): Broadcast[] => {
    const liveBroadcasts = broadcasts
      .filter((broadcastEl) => broadcastEl.isLive)
      // Non-listed events (unlisted / only_me) must never surface as the
      // channel's live broadcast — the fallback takes precedence instead.
      .filter((broadcastEl) =>
        isBroadcastFromListedEvent(broadcastEl, events, privateEvents)
      )
      .sort(
        (broadcast1, broadcast2) =>
          Number(broadcast1.fallback) - Number(broadcast2.fallback)
      );
    return liveBroadcasts;
  }
);

export const nextLiveBroadcastSelector = createSelector(
  liveBroadcastsSelector,
  (broadcasts): Broadcast | null => {
    return broadcasts[0];
  }
);

export const fallbackBroadcastSelector = createSelector(
  [(state: RootState) => state.broadcasts.broadcasts],
  (broadcasts): Broadcast | null => {
    const fallbackBroadcasts = broadcasts.filter(
      (broadcastEl) => broadcastEl.fallback
    );
    return fallbackBroadcasts[0];
  }
);

export const broadcastForEventSelector = createSelector(
  [
    (state: RootState) => state.broadcasts.broadcasts,
    (state: RootState, eventId?: string | null) => eventId
  ],
  (broadcasts, eventId): Broadcast | null => {
    return (
      broadcasts.find((broadcastEl) => broadcastEl?.eventId === eventId) || null
    );
  }
);

export const currentEventBroadcastSelector = createSelector(
  [
    (state: RootState) => state.events.currentEventId,
    (state: RootState) => state.broadcasts.broadcasts
  ],
  (eventId, broadcasts): Broadcast | null => {
    return (
      broadcasts.find((broadcastEl) => broadcastEl?.eventId === eventId) || null
    );
  }
);

export const currentBroadcastFinishedSelector = createSelector(
  currentEventBroadcastSelector,
  (broadcast): boolean => {
    return broadcast?.finished || false;
  }
);

export const currentBroadcastListenerCountSelector = createSelector(
  currentEventBroadcastSelector,
  (broadcast): number | null => {
    return broadcast?.listenerCount || null;
  }
);

export const currentBroadcastHeartCountSelector = createSelector(
  currentEventBroadcastSelector,
  (broadcast): number | null => {
    if (!broadcast) {
      return null;
    }
    return broadcast.heartCount || 0;
  }
);
